import React, { Component, ErrorInfo, ReactNode, useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, ScrollView, Animated, Easing } from 'react-native';
import { useThemeSafe } from '../context/themeContext';
import Icon from './icon';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

interface FallbackProps {
  error: Error | null;
  onReset: () => void;
}

const isWeb = Platform.OS === 'web';
// Animated transforms/opacity can use the native driver everywhere except web.
const NATIVE = !isWeb;
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});

function ErrorFallback({ error, onReset }: FallbackProps) {
  const { theme } = useThemeSafe();
  const { colors } = theme;
  const isDev = typeof __DEV__ !== 'undefined' && __DEV__;

  // Entrance (fade + rise + settle), a looping danger halo, and a gentle icon float.
  const enter = useRef(new Animated.Value(0)).current;
  const pulse = useRef(new Animated.Value(0)).current;
  const float = useRef(new Animated.Value(0)).current;
  const press = useRef(new Animated.Value(0)).current;
  const [hover, setHover] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    Animated.spring(enter, { toValue: 1, useNativeDriver: NATIVE, friction: 8, tension: 64 }).start();
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 1900, easing: Easing.out(Easing.ease), useNativeDriver: NATIVE }),
        Animated.timing(pulse, { toValue: 0, duration: 0, useNativeDriver: NATIVE }),
      ]),
    ).start();
    Animated.loop(
      Animated.sequence([
        Animated.timing(float, { toValue: 1, duration: 2600, easing: Easing.inOut(Easing.ease), useNativeDriver: NATIVE }),
        Animated.timing(float, { toValue: 0, duration: 2600, easing: Easing.inOut(Easing.ease), useNativeDriver: NATIVE }),
      ]),
    ).start();
  }, [enter, pulse, float]);

  const cardAnim = {
    opacity: enter,
    transform: [
      { translateY: enter.interpolate({ inputRange: [0, 1], outputRange: [18, 0] }) },
      { scale: enter.interpolate({ inputRange: [0, 1], outputRange: [0.95, 1] }) },
    ],
  };
  const haloAnim = {
    opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.5, 0] }),
    transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.85, 1.9] }) }],
  };
  const floatAnim = {
    transform: [{ translateY: float.interpolate({ inputRange: [0, 1], outputRange: [0, -5] }) }],
  };
  const btnScale = { transform: [{ scale: press.interpolate({ inputRange: [0, 1], outputRange: [1, 0.97] }) }] };

  return (
    <View style={[styles.container, { backgroundColor: colors.paper }]}>
      {/* Ambient backdrop — soft tinted glows behind the card (web). */}
      {isWeb && (
        <>
          <View style={[styles.blob, styles.blobTop, web({ backgroundImage: `radial-gradient(circle, ${colors.danger}, transparent 70%)`, filter: 'blur(90px)' })]} pointerEvents="none" />
          <View style={[styles.blob, styles.blobBottom, web({ backgroundImage: `radial-gradient(circle, ${colors.accent}, transparent 70%)`, filter: 'blur(100px)' })]} pointerEvents="none" />
        </>
      )}

      <Animated.View
        style={[
          styles.card,
          { backgroundColor: colors.card, borderColor: colors.line },
          cardAnim,
          isWeb
            ? ({ boxShadow: '0 24px 70px -24px rgba(16,24,40,0.45), 0 2px 8px rgba(16,24,40,0.06)', backdropFilter: 'blur(8px)' } as any)
            : { elevation: 14, shadowColor: '#0F172A', shadowOpacity: 0.18, shadowRadius: 30, shadowOffset: { width: 0, height: 16 } },
        ]}
      >
        {/* Icon with pulsing danger halo */}
        <View style={styles.iconWrap}>
          <Animated.View
            pointerEvents="none"
            style={[styles.halo, { backgroundColor: colors.danger }, haloAnim]}
          />
          <Animated.View
            style={[
              styles.iconCircle,
              { borderColor: colors.dangerLine ?? colors.line },
              floatAnim,
              isWeb
                ? ({ backgroundImage: `linear-gradient(150deg, ${colors.danger}, ${colors.dangerSoft})`, boxShadow: `0 10px 28px -8px ${colors.danger}99` } as any)
                : { backgroundColor: colors.dangerSoft },
            ]}
          >
            <Icon name="alert-circle" size={32} color={isWeb ? '#fff' : colors.danger} />
          </Animated.View>
        </View>

        <Text style={[styles.title, { color: colors.ink }]}>Something went wrong</Text>
        <Text style={[styles.message, { color: colors.ink3 }]}>
          The app hit an unexpected error — likely a temporary connection or session hiccup. A quick refresh usually puts things right.
        </Text>

        {isDev && !!error && (
          <View style={styles.detailsWrap}>
            <TouchableOpacity
              onPress={() => setShowDetails((s) => !s)}
              activeOpacity={0.7}
              style={[styles.detailsToggle, { borderColor: colors.line, backgroundColor: colors.paper2 }, web({ cursor: 'pointer' })]}
            >
              <Icon name={showDetails ? 'chev-u' : 'chev-d'} size={13} color={colors.ink3} />
              <Text style={[styles.detailsToggleText, { color: colors.ink3 }]}>
                {showDetails ? 'Hide error details' : 'Show error details'}
              </Text>
            </TouchableOpacity>
            {showDetails && (
              <ScrollView style={[styles.errorScroll, { backgroundColor: colors.paper2, borderColor: colors.line }]}>
                <Text style={[styles.errorText, { color: colors.danger }]}>{error?.stack ?? error?.toString()}</Text>
              </ScrollView>
            )}
          </View>
        )}

        <Animated.View style={[{ width: '100%' }, btnScale]}>
          <TouchableOpacity
            onPress={onReset}
            onPressIn={() => Animated.spring(press, { toValue: 1, useNativeDriver: NATIVE, friction: 6 }).start()}
            onPressOut={() => Animated.spring(press, { toValue: 0, useNativeDriver: NATIVE, friction: 6 }).start()}
            activeOpacity={0.9}
            style={[
              styles.button,
              { backgroundColor: colors.accent },
              isWeb
                ? ({
                    cursor: 'pointer',
                    backgroundImage: `linear-gradient(135deg, ${colors.accent}, ${colors.accentInk})`,
                    boxShadow: hover ? `0 12px 28px -8px ${colors.accent}cc` : `0 6px 16px -6px ${colors.accent}99`,
                    transition: 'box-shadow .18s ease',
                  } as any)
                : null,
            ]}
            {...(isWeb ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as any) : {})}
          >
            <Icon name="refresh" size={16} color={colors.onAccent} />
            <Text style={[styles.buttonText, { color: colors.onAccent }]}>Refresh application</Text>
          </TouchableOpacity>
        </Animated.View>

        <Text style={[styles.helper, { color: colors.ink4 }]}>
          If this keeps happening, please contact support.
        </Text>
      </Animated.View>
    </View>
  );
}

export default class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (Platform.OS === 'web') {
      window.location.reload();
    }
  };

  public render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}),
  },
  blob: {
    position: 'absolute',
    width: 460,
    height: 460,
    borderRadius: 460,
    opacity: 0.16,
  },
  blobTop: { top: -160, right: -120 },
  blobBottom: { bottom: -180, left: -140 },
  card: {
    borderRadius: 28,
    paddingHorizontal: 36,
    paddingVertical: 40,
    width: '100%',
    maxWidth: 440,
    alignItems: 'center',
    borderWidth: 1,
  },
  iconWrap: {
    width: 96,
    height: 96,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 22,
  },
  halo: {
    position: 'absolute',
    width: 72,
    height: 72,
    borderRadius: 72,
  },
  iconCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
  },
  title: {
    fontSize: 23,
    fontWeight: '800',
    marginBottom: 10,
    textAlign: 'center',
    letterSpacing: -0.4,
  },
  message: {
    fontSize: 14.5,
    textAlign: 'center',
    lineHeight: 23,
    marginBottom: 26,
    maxWidth: 360,
  },
  detailsWrap: {
    width: '100%',
    marginBottom: 22,
  },
  detailsToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'center',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    borderWidth: 1,
  },
  detailsToggleText: {
    fontSize: 12.5,
    fontWeight: '600',
  },
  errorScroll: {
    maxHeight: 160,
    width: '100%',
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
    marginTop: 12,
  },
  errorText: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    paddingHorizontal: 24,
    paddingVertical: 15,
    borderRadius: 14,
    width: '100%',
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#fff',
    letterSpacing: 0.2,
  },
  helper: {
    fontSize: 12.5,
    textAlign: 'center',
    marginTop: 16,
  },
});
